// src/pages/work-order-management-system/components/hooks/useBudgetForm.js
import { useEffect, useMemo, useState } from 'react';

const todayIso = () => new Date().toISOString().split('T')[0];

const safeStr = (v) => (v == null ? '' : String(v));
const upper = (v) => safeStr(v).toUpperCase();

const pickRawDatosGenerales = (prefill) => {
    if (!prefill) return null;

    // Soportar varios formatos:
    // 1) raw directo ya con datosGenerales
    if (prefill.datosGenerales && typeof prefill.datosGenerales === 'object') {
        return prefill.datosGenerales;
    }

    // 2) prefill ya es datosGenerales plano
    const hasAnyDGField =
        'cliente' in prefill ||
        'unidad' in prefill ||
        'motor' in prefill ||
        'modelo' in prefill ||
        'vin' in prefill ||
        'placas' in prefill ||
        'kilometraje' in prefill ||
        'tipoServicio' in prefill ||
        'fecha' in prefill ||
        'factura' in prefill ||
        'observaciones' in prefill;

    if (hasAnyDGField) return prefill;

    // 3) por si te pasan algo envuelto
    if (prefill.meta?.rawData?.datosGenerales) return prefill.meta.rawData.datosGenerales;

    return null;
};

/**
 * Maneja los datos generales del presupuesto / orden
 * ✅ Prefill desde:
 *   - initialData (historial) => raw con datosGenerales
 *   - selectedWorkOrder (backend)
 */
export const useBudgetForm = (selectedWorkOrder, initialData = null) => {
    const [datosGenerales, setDatosGenerales] = useState({
        cliente: '',
        unidad: '',
        motor: '',
        modelo: '',
        vin: '',
        placas: '',
        kilometraje: '',
        tipoServicio: '',
        fecha: todayIso(),
        factura: '',
        observaciones: ''
    });

    // ✅ Prefill con prioridad: initialData (editar historial) > selectedWorkOrder
    useEffect(() => {
        // 1) Prefill desde historial (raw)
        const dg = pickRawDatosGenerales(initialData);
        if (dg) {
            setDatosGenerales((prev) => ({
                ...prev,
                ...dg,
                // normalizaciones suaves
                vin: upper(dg.vin ?? prev.vin),
                placas: upper(dg.placas ?? prev.placas),
                fecha: safeStr(dg.fecha || prev.fecha || todayIso())
            }));
            return;
        }

        // 2) Prefill desde selectedWorkOrder (modo clásico)
        if (!selectedWorkOrder) return;

        setDatosGenerales((prev) => {
            const flat = selectedWorkOrder || {};
            const vehicle = flat.vehicle || {};

            const next = {
                ...prev,
                cliente: safeStr(flat.client || flat.clientName || prev.cliente),
                unidad: safeStr(flat.unit || vehicle.unit || prev.unidad),
                motor: safeStr(flat.engine || vehicle.engine || prev.motor),
                modelo: safeStr(flat.model || vehicle.model || prev.modelo),
                vin: upper(flat.vin || vehicle.vin || prev.vin),
                placas: upper(flat.plates || vehicle.plates || prev.placas),
                kilometraje: safeStr(
                    typeof flat.mileage !== 'undefined'
                        ? flat.mileage
                        : typeof vehicle.mileage !== 'undefined'
                            ? vehicle.mileage
                            : prev.kilometraje
                ),
                tipoServicio: safeStr(flat.serviceType || prev.tipoServicio),
                fecha: safeStr(flat.date || prev.fecha || todayIso()),
                factura: safeStr(flat.invoiceNumber || prev.factura),
                observaciones: safeStr(flat.additionalObservations || prev.observaciones)
            };

            return next;
        });
    }, [selectedWorkOrder, initialData]);

    const handleChangeDatosGenerales = (field, value) => {
        setDatosGenerales((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    // ✅ folio también debe tomar initialData
    const folio = useMemo(() => {
        // si viene del historial normalmente: factura o folio dentro de datosGenerales
        const fromInitial =
            initialData?.folio ||
            initialData?.workOrderNumber ||
            initialData?.id ||
            initialData?.datosGenerales?.factura ||
            initialData?.factura;

        const fromSelected =
            selectedWorkOrder?.workOrderNumber ||
            selectedWorkOrder?.id ||
            selectedWorkOrder?.invoiceNumber;

        return (
            safeStr(fromInitial || fromSelected || datosGenerales.factura || 'NUEVA-ORDEN') ||
            'NUEVA-ORDEN'
        );
    }, [initialData, selectedWorkOrder, datosGenerales.factura]);

    return {
        datosGenerales,
        handleChangeDatosGenerales,
        folio
    };
};
